import { createContext, useContext, useId, useMemo, useRef, useState } from 'react';
import type { ComponentProps, ReactElement } from 'react';
import cx from 'clsx';
import { Tooltip } from 'react-tooltip';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListItem,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react';
import { Box, Button } from '@ff-it/ui';
import { humanizeName } from 'utilities';
import type { ControlledTableColumnConfig, PinSide } from './useColumnConfig';
import {
  activeRow,
  cell,
  cellDisabled,
  columnButton,
  columnDot,
  columnLabel,
  hiddenLabel,
  highlightIcon,
  list,
  menuDivider,
  panel,
  pins,
  row,
} from './ColumnConfigPopover.css';

type IconName = ComponentProps<typeof Button>['icon'];

function columnLabelFor(c: ControlledTableColumnConfig<any>): string {
  if (c.configLabel) {
    return c.configLabel;
  }
  if (typeof c.column.content === 'string' && c.column.content) {
    return c.column.content;
  }
  if (typeof c.column.title === 'string' && c.column.title) {
    return c.column.title;
  }
  return humanizeName(String(c.column.key));
}

const GridContext = createContext<{
  activeIndex: number | null;
  tooltipId: string;
  getItemProps: (props?: Record<string, unknown>) => Record<string, unknown>;
}>({ activeIndex: null, tooltipId: '', getItemProps: () => ({}) });

function Cell({
  label,
  icon,
  active,
  highlight,
  disabled,
  tooltip,
  onActivate,
}: {
  label?: string;
  icon: IconName;
  active?: boolean;
  highlight?: boolean;
  disabled?: boolean;
  tooltip?: string;
  onActivate: () => void;
}): ReactElement {
  const { ref, index } = useListItem({ label });
  const { activeIndex, tooltipId, getItemProps } = useContext(GridContext);
  const tabbable = activeIndex === index || (activeIndex === null && index === 0);
  return (
    <Button
      ref={ref}
      size="xs"
      variant="ghost"
      icon={icon}
      active={active}
      aria-disabled={disabled || undefined}
      aria-label={tooltip}
      data-tooltip-id={tooltip ? tooltipId : undefined}
      data-tooltip-content={tooltip}
      className={cx(cell, highlight && highlightIcon, disabled && cellDisabled)}
      tabIndex={tabbable ? 0 : -1}
      {...getItemProps({ onClick: disabled ? undefined : onActivate })}
    />
  );
}

/**
 * A column picker rendered as an accessible layout grid: each row is [visibility, pin-left, pin-right]
 * toggle-buttons. Arrow keys move in 2-D, type-ahead jumps by name, Esc closes and returns focus.
 */
export function ColumnConfigPopover({
  configuredColumns,
  toggleColumn,
  pinColumn,
  resetColumns,
  className,
  showPins = true,
}: {
  configuredColumns: ControlledTableColumnConfig<any>[];
  toggleColumn: (key: string) => void;
  pinColumn: (key: string, side: PinSide | null) => void;
  resetColumns: () => void;
  className?: string;
  showPins?: boolean;
}): ReactElement {
  const tooltipId = useId();
  // the row buttons' tooltips are their own instance, rendered inside the popover (mounted only while
  // it's open) — separate from the trigger's "N hidden" tooltip
  const cellTooltipId = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  // always-visible columns are listed too — they can't be hidden, but they can be pinned
  const items = useMemo(
    () =>
      configuredColumns.map((c) => ({
        key: String(c.column.key),
        label: columnLabelFor(c),
        visible: c.visible,
        pinned: c.pinned,
        required: c.required === true,
      })),
    [configuredColumns],
  );
  const cols = showPins ? 3 : 1;
  const hiddenCount = items.filter((c) => !c.visible).length;
  const pinnedCount = showPins ? items.filter((c) => c.pinned).length : 0;
  // the trigger badge + Reset reflect any non-default config (hidden or pinned)
  const summary = [hiddenCount && `${hiddenCount} hidden`, pinnedCount && `${pinnedCount} pinned`]
    .filter(Boolean)
    .join(', ');

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const listNav = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    cols,
    orientation: 'both',
    loop: true,
    focusItemOnOpen: 'auto',
    focusItemOnHover: false,
  });
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    onMatch: open ? setActiveIndex : undefined,
    enabled: open,
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    role,
    listNav,
    typeahead,
  ]);

  const activeRowIndex = activeIndex === null ? -1 : Math.floor(activeIndex / cols);

  return (
    <>
      <Box
        as="span"
        className={cx(columnButton, className)}
        data-tooltip-id={summary ? tooltipId : undefined}
        data-tooltip-content={summary || undefined}
      >
        <Button
          ref={refs.setReference}
          size="sm"
          variant="ghost"
          icon="table-columns"
          testId="column-config"
          {...getReferenceProps()}
        />
        {summary ? <Box as="span" className={columnDot} /> : null}
      </Box>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div ref={refs.setFloating} className={panel} style={floatingStyles} {...getFloatingProps()}>
              <Box px="3" pt="3" pb="2" fontSize="xs" fontWeight="semibold" color="secondary" textTransform="uppercase">
                Columns
              </Box>
              <GridContext.Provider value={{ activeIndex, tooltipId: cellTooltipId, getItemProps }}>
                <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                  <div role="grid" className={list}>
                    {items.map((item, rowIndex) => (
                      <div role="row" key={item.key} className={cx(row, rowIndex === activeRowIndex && activeRow)}>
                        <Cell
                          label={item.label}
                          icon={item.visible ? 'eye' : 'eye-slash'}
                          disabled={item.required}
                          highlight={!item.visible}
                          tooltip={item.required ? 'Always visible' : item.visible ? 'Hide column' : 'Show column'}
                          onActivate={() => toggleColumn(item.key)}
                        />
                        <span className={cx(columnLabel, !item.visible && hiddenLabel)}>{item.label}</span>
                        {showPins && (
                          <span className={pins}>
                            <Cell
                              icon="left-to-line"
                              active={item.pinned === 'left'}
                              highlight={item.pinned === 'left'}
                              disabled={!item.visible}
                              tooltip={item.pinned === 'left' ? 'Unpin' : 'Pin left'}
                              onActivate={() => pinColumn(item.key, item.pinned === 'left' ? null : 'left')}
                            />
                            <Cell
                              icon="right-to-line"
                              active={item.pinned === 'right'}
                              highlight={item.pinned === 'right'}
                              disabled={!item.visible}
                              tooltip={item.pinned === 'right' ? 'Unpin' : 'Pin right'}
                              onActivate={() => pinColumn(item.key, item.pinned === 'right' ? null : 'right')}
                            />
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </FloatingList>
              </GridContext.Provider>
              <Box className={menuDivider} />
              <Box px="1" py="1">
                <Button
                  variant="ghost"
                  size="xs"
                  icon="arrow-rotate-left"
                  className="w-100"
                  disabled={!summary}
                  onClick={resetColumns}
                >
                  Reset to default
                </Button>
              </Box>
              <Tooltip id={cellTooltipId} openEvents={{ mouseenter: true }} />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
      {summary ? <Tooltip id={tooltipId} /> : null}
    </>
  );
}
